import React, { useState, useEffect } from 'react';
import {
    Box,
    Text,
    Switch,
    Select,
    VStack,
    HStack,
    Button,
    Alert,
    AlertIcon,
    FormControl,
    FormLabel,
    Divider,
    useColorModeValue,
    Tag,
    useToast,
    Spinner
} from '@chakra-ui/react';
import { LuInfo } from 'react-icons/lu';
import axios from 'axios';

const DuplicateSetting = ({ customer }) => {
    const [isEnabled, setIsEnabled] = useState(true);
    const [duration, setDuration] = useState(60000); // 5 minutes in milliseconds
    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [customerId] = useState(1); // You can get this from your auth context or props

    const toast = useToast();

    // Color mode values
    const infoBgColor = useColorModeValue('blue.50', 'blue.900');
    const textColor = useColorModeValue('gray.600', 'gray.300');

    // Duration options in milliseconds
    const durationOptions = [
        { label: '1 minute', value: 60000 },
        { label: '3 minutes', value: 180000 },
        { label: '5 minutes', value: 300000 },
        { label: '10 minutes', value: 600000 },
        { label: '15 minutes', value: 900000 },
        { label: '30 minutes', value: 1800000 },
        { label: '45 minutes', value: 2700000 },
        { label: '1 hour', value: 3600000 },
        { label: '2 hours', value: 7200000 },
        { label: '3 hours', value: 10800000 }
    ];

    // Import this function or define it in your utils
    const getApiHeaders = () => ({

        "x-access-token": localStorage.getItem('user'),
        'Content-Type': 'application/json'
    });



    useEffect(() => {

        if (customer?.duplicate_message > 0) {
            setDuration(customer?.duplicate_message)
            setIsEnabled(true)
        } else {
            setDuration(customer?.duplicate_message)
            setIsEnabled(false)
        }
    }, [customer.duplicate_message])


    // Save settings
    const handleSave = async () => {
        try {
            setLoading(true);

            const payload = {
                id: customer?.id,
                duplicate_message: isEnabled ? duration : 0 // Set to 0 if disabled
            };
            const url = `${process.env.REACT_APP_API_URL}/customer/duplicate-messsage`;

            const response = await axios.post(url, payload, {

                headers: getApiHeaders(),

            });

            if (response?.status === 200) {
                toast({
                    title: 'Success',
                    description: 'Duplicate message settings saved successfully',
                    status: 'success',
                    duration: 3000,
                    isClosable: true,
                });
            }



        } catch (error) {
            console.error('Error saving settings:', error);
            toast({
                title: 'Error',
                description: 'Failed to save duplicate message settings',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setLoading(false);
        }
    };

    const handleReset = () => {
        setIsEnabled(true);
        setDuration(300000); // Reset to 5 minutes
    };

    const formatDurationLabel = (milliseconds) => {
        const minutes = milliseconds / 60000;
        if (minutes < 60) {
            return `${minutes} min${minutes !== 1 ? 's' : ''}`;
        } else {
            const hours = minutes / 60;
            return `${hours} hour${hours !== 1 ? 's' : ''}`;
        }
    };




    return (
        <Box>
            <VStack spacing={3} align="stretch">
                {/* Header */}
                <Box mb={1}>
                    <Text fontSize="sm" fontWeight="bold" mb={1}>
                        Duplicate Message Settings
                    </Text>
                    <Text color={textColor} fontSize="xs">
                        Configure duplicate message detection to prevent sending identical content to the same recipient within a specified time window.
                    </Text>
                </Box>

                {/* Enable Duplicate Detection */}
                <FormControl>
                    <HStack justify="space-between" align="center">
                        <Box>
                            <HStack spacing={2} mb={1}>
                                <FormLabel htmlFor="duplicate-detection" mb={0} fontSize="sm" fontWeight="medium">
                                    Enable Duplicate Detection
                                </FormLabel>
                                <Tag colorScheme={isEnabled ? "green" : "gray"} fontSize="xs">
                                    {isEnabled ? "Enabled" : "Disabled"}
                                </Tag>
                            </HStack>
                            <Text color={textColor} fontSize="xs">
                                When enabled, the system will automatically detect and prevent duplicate messages from being sent to the same recipient.
                            </Text>
                        </Box>
                        <Switch
                            id="duplicate-detection"
                            colorScheme="blue"
                            isChecked={isEnabled}
                            onChange={(e) => {
                                const checked = e.target.checked;
                                setIsEnabled(checked);

                                // ✅ If enabling and duration is 0 or undefined, set default duration (5 minutes)
                                if (checked && (!duration || duration === 0)) {
                                    setDuration(60000); // 5 minutes
                                }
                            }}
                        />

                    </HStack>
                </FormControl>
                {isEnabled && (
                    <>
                        {/* Duplicate Check Duration */}
                        <FormControl isDisabled={!isEnabled}>
                            <FormLabel fontSize="sm" fontWeight="medium" mb={1}>
                                Duplicate Check Duration
                            </FormLabel>
                            <Text color={textColor} fontSize="xs" mb={2}>
                                Messages with identical content sent to the same recipient within this time frame will be considered duplicates and blocked.
                            </Text>
                            <Select
                                value={duration}
                                onChange={(e) => setDuration(Number(e.target.value))}
                                maxWidth="200px"
                                size="sm"
                            >
                                {durationOptions.map((option) => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </Select>
                        </FormControl>

                        {/* How Duplicate Detection Works */}
                        <Alert status="info" bg={infoBgColor} borderRadius="md">
                            <AlertIcon as={LuInfo} />
                            <Box>
                                <Text fontWeight="medium" mb={1} fontSize="sm">
                                    How Duplicate Detection Works
                                </Text>
                                <VStack align="start" spacing={0.5} fontSize="xs">
                                    <Text>• Messages are considered duplicates if they have identical content and are sent to the same recipient</Text>
                                    <Text>• The system checks for duplicates within the specified time window</Text>
                                    <Text>• Duplicate messages are automatically blocked to prevent spam and reduce costs</Text>
                                    <Text>• Currently configured duration: <strong>{formatDurationLabel(duration)}</strong></Text>
                                </VStack>
                            </Box>
                        </Alert>
                    </>
                )}

                {/* Action Buttons */}
                <HStack spacing={4} pt={2}>
                    <Button
                        colorScheme="blue"
                        size="sm"
                        onClick={handleSave}
                        px={6}
                        isLoading={loading}
                        loadingText="Saving..."
                    >
                        Save Settings
                    </Button>

                </HStack>
            </VStack>
        </Box>
    );
};

export default DuplicateSetting;